/**
 * PushToggleButton — the Pause / Resume button of the drawer's push status.
 * Split out of KillSwitchBanner (over the function limit); same markup.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Pause, Play } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { Tooltip } from '@/components/ui/Tooltip';

export interface PushToggleButtonProps {
  paused: boolean;
  /** Tracker display name, e.g. "Voluum". */
  tracker: string;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
}

const ACTIVE_CLASS = 'h-5 shrink-0 gap-0.5 px-1.5 text-[10px]';
const PAUSED_CLASS =
  'h-5 shrink-0 gap-0.5 border-destructive/40 px-1.5 text-[10px] text-destructive hover:bg-destructive/10';

const PushToggleButton: FC<PushToggleButtonProps> = ({ paused, tracker, onToggle, disabled }) => {
  const verb = paused ? 'Resume' : 'Pause';
  const label = `${verb} weight pushes to ${tracker}`;
  const Icon = paused ? Play : Pause;
  return (
    <Tooltip content={label} side="bottom">
      <Button
        size="sm"
        variant="outline"
        onClick={() => onToggle(!paused)}
        disabled={disabled}
        type="button"
        className={paused ? PAUSED_CLASS : ACTIVE_CLASS}
        aria-label={label}
      >
        <Icon className="h-2.5 w-2.5" aria-hidden />
        {verb}
      </Button>
    </Tooltip>
  );
};

export default PushToggleButton;
